import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, StyleSheet, Alert, Image, useWindowDimensions, Platform, Modal } from 'react-native';
import { SafeAreaProvider, SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from './theme';
import storage from './lib/storage';
import { ensureDefaultCategories } from './features/transactions/transactionsRepository';
import CategoriesManager from './features/transactions/components/CategoriesManager';
import LoginForm from './features/auth/LoginForm';
import SignupForm from './features/auth/SignupForm';
import QuarterDashboard from './features/dashboard/QuarterDashboard';
import TransactionForm from './features/transactions/components/TransactionForm';
import TransactionsList from './features/transactions/TransactionsList';
import ChangePasswordForm from './features/auth/ChangePasswordForm';
import * as profileRepo from './features/profile/profileRepository';
import EditProfileForm from './features/profile/EditProfileForm';
import { I18nContext, useI18n } from './i18n/useI18n';
import AppButton from './components/AppButton';

const BRAND_ICON = require('./assets/branding/icono.png');

function Text3D({ children, style }: { children: string; style?: any }) {
  return (
    <View style={{ position: 'relative' }}>
      <Text style={[style, styles.text3dBack]}>{children}</Text>
      <Text style={[style, styles.text3dFront]}>{children}</Text>
    </View>
  );
}

export default function KakeiboApp() {
  return (
    <SafeAreaProvider>
      <KakeiboAppInner />
    </SafeAreaProvider>
  );
}

function KakeiboAppInner() {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // Use smaller responsive padding (about half of the previous defaults) to maximize usable space.
  const edgePadding = width < 380 ? 5 : width < 700 ? 6 : 10;
  const cardPadding = width < 380 ? 12 : 16;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // load stored session (token + user)
  useEffect(() => {
    let mounted = true;
    storage.getItem('kakeibo_user').then(raw => {
      if (!mounted) return;
      if (raw) {
        try {
          setUser(raw);
        } catch (_) {}
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Try to refresh profile from backend (cookie or bearer token)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const body = await profileRepo.getProfile();
        if (!mounted) return;
        if (body && body.user) {
          setUser(body.user);
          try { await storage.setItem('kakeibo_user', body.user || {}); } catch (_) {}
        }
      } catch (_) {
        // ignore unauthorized / network errors on startup
      }
    })();
    return () => { mounted = false; };
  }, []);

  // create default categories for new users (client-side, per-user)
  useEffect(() => {
    if (user && user.id) {
      ensureDefaultCategories(user.id).catch(() => {});
    }
  }, [user]);

  const [showCategories, setShowCategories] = React.useState(false);
  const [showSignup, setShowSignup] = React.useState(false);
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [showEditProfile, setShowEditProfile] = React.useState(false);

  async function handleAuthSuccess(payload: { token?: string; user?: any }) {
    setLoading(true);
    try {
      // Token storage should never block UI transition.
      try {
        if (payload.token) {
          await storage.saveAuthToken(payload.token);
        } else {
          await storage.clearAuthToken?.();
        }
      } catch (_) {
        // ignore token persistence errors (common on web)
      }

      try {
        await storage.setItem('kakeibo_user', payload.user || {});
      } catch (_) {
        // ignore persistence errors; keep in-memory session
      }

      setUser(payload.user || null);
      Alert.alert('Signed in', `Hello ${payload.user?.email || 'user'}`);
    } catch (err: any) {
      Alert.alert('Error', err.message || String(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    try {
      setLoading(true);
      await storage.clearAuthToken?.();
      await storage.removeItem('kakeibo_user');
      setUser(null);
      Alert.alert('Signed out');
    } catch (err: any) {
      Alert.alert('Error', err.message || String(err));
    } finally { setLoading(false); }
  }

  async function handleAccountDeleted() {
    try {
      setLoading(true);
      await storage.clearAuthToken?.();
      await storage.removeItem('kakeibo_user');
      setShowEditProfile(false);
      setShowSettings(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }
  const [addType, setAddType] = React.useState<'expense' | 'income' | null>(null);
  const [editingTransaction, setEditingTransaction] = React.useState<any | null>(null);
  const [lang, setLang] = React.useState<'en'|'es'>('en');
  const [activeTab, setActiveTab] = React.useState<'dashboard'|'transactions'>('dashboard');
  const { t } = useI18n(lang);
  const isFullscreenFlow = !!addType || !!editingTransaction;
  const basePad = isFullscreenFlow ? 0 : edgePadding;

  async function handleSaveProfile(nextUser: any) {
    setUser(nextUser);
    try {
      await storage.setItem('kakeibo_user', nextUser || {});
    } catch (_) {
      // ignore
    }
  }

  return (
    <I18nContext.Provider value={{ lang, setLang }}>
    <View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.background,
          paddingTop: insets.top + basePad,
          paddingBottom: insets.bottom + basePad,
          paddingLeft: insets.left + basePad,
          paddingRight: insets.right + basePad,
        },
      ]}
    > 

      <View style={[styles.content, addType ? styles.contentStretch : styles.contentCenter]}>

      {user && !addType ? null : null}

      {user ? (
        addType ? (
          // render transaction form fullscreen; remove outer padding so it can use full width on mobile
          <View style={{ flex: 1, padding: 0, alignItems: 'stretch', justifyContent: 'flex-start' }}>
            <TransactionForm
              userId={user.id}
              defaultType={addType}
              onClose={() => setAddType(null)}
              lang={lang}
              presentation="fullscreen"
            />
          </View>
        ) : editingTransaction ? (
          // render edit transaction form fullscreen to avoid visual overlap with the signed-in card
          <View style={{ flex: 1, padding: 0, alignItems: 'stretch', justifyContent: 'flex-start' }}>
            <TransactionForm
              userId={user.id}
              defaultType={editingTransaction.type}
              initialTransaction={editingTransaction}
              onClose={() => setEditingTransaction(null)}
              lang={lang}
              presentation="fullscreen"
            />
          </View>
        ) : (
        <View style={[styles.card, styles.cardFill, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, padding: cardPadding }]}> 
          <View style={[styles.cardHeader, Platform.OS === 'web' ? styles.cardHeaderWeb : null]}>
            <View style={styles.headerIconBox}>
              <Image source={BRAND_ICON} style={styles.headerIcon} />
            </View>
            <View style={styles.headerRight}>
              <AppButton
                title={lang === 'en' ? 'ES' : 'EN'}
                tone="secondary"
                variant="outline"
                onPress={() => setLang(l => l === 'en' ? 'es' : 'en')}
                style={styles.langButton}
                textStyle={styles.langButtonText}
              />
              <View style={{ width: 8 }} />
              <AppButton
                title="☰"
                tone="secondary"
                variant="outline"
                onPress={() => setShowSettings(true)}
                style={styles.menuButton}
                textStyle={styles.menuButtonText}
              />
            </View>
          </View>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>{t('signedInAs')}</Text>
          <Text style={{ color: theme.colors.textPrimary }}>{user.email}</Text>
          <View style={{ height: 12 }} />
          {/* Row 1: add buttons, centered inside the card */}
          <View style={{ alignItems: 'center', marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <AppButton
                title={lang === 'es' ? '↓ Agregar gasto' : '↓ Add expense'}
                onPress={() => setAddType('expense')}
                tone="error"
                style={{ marginRight: 8 }}
              />
              <AppButton
                title={lang === 'es' ? '↑ Agregar ingreso' : '↑ Add income'}
                onPress={() => setAddType('income')}
                tone="success"
              />
            </View>
          </View>

          {/* Row 2: tabs, centered and uniform */}
          <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginBottom: 8, width: '100%' }}>
            <View style={{ flex: 1, maxWidth: 220 }}>
              <AppButton
                title={`📊 ${t('dashboardTab')}`}
                onPress={() => setActiveTab('dashboard')}
                tone="primary"
                variant={activeTab === 'dashboard' ? 'solid' : 'outline'}
                style={{ width: '100%' }}
              />
            </View>
            <View style={{ width: 8 }} />
            <View style={{ flex: 1, maxWidth: 220 }}>
              <AppButton
                title={`🧾 ${t('transactionsTab')}`}
                onPress={() => setActiveTab('transactions')}
                tone="primary"
                variant={activeTab === 'transactions' ? 'solid' : 'outline'}
                style={{ width: '100%' }}
              />
            </View>
          </View>
          {activeTab === 'dashboard' ? (
            <QuarterDashboard userId={user.id} lang={lang} />
          ) : (
            <TransactionsList
              userId={user.id}
              lang={lang}
              onEditTransaction={(txn) => {
                setEditingTransaction(txn);
              }}
            />
          )}
        </View>
        )
      ) : (
        <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border, padding: cardPadding }]}> 
          <View style={[styles.cardHeader, Platform.OS === 'web' ? styles.cardHeaderWeb : null]}>
            <View style={styles.headerIconBox}>
              <Image source={BRAND_ICON} style={styles.headerIcon} />
            </View>
            <AppButton
              title={lang === 'en' ? 'ES' : 'EN'}
              tone="secondary"
              variant="outline"
              onPress={() => setLang(l => l === 'en' ? 'es' : 'en')}
              style={styles.langButton}
              textStyle={styles.langButtonText}
            />
          </View>
          <View style={[styles.logoSlot, { borderColor: theme.colors.border, backgroundColor: theme.colors.background }]}>
            <View style={{ alignItems: 'center' }}>
              <Text3D style={[styles.headerTitle, { color: theme.colors.textPrimary }]}>{t('headerTitle')}</Text3D>
              <Text3D style={[styles.headerSubtitle, { color: theme.colors.textSecondary }]}>{t('headerSubtitle')}</Text3D>
            </View>
          </View>
          <View style={{ height: 12 }} />
          <View style={[styles.authHeader, { borderColor: showSignup ? theme.colors.success : theme.colors.primary }]}> 
            <Text style={[styles.authHeaderText, { color: showSignup ? theme.colors.success : theme.colors.primary }]}>
              {showSignup ? t('signUp') : t('signIn')}
            </Text>
          </View>
          <View style={{ height: 12 }} />
          {!showSignup ? (
            <>
              <LoginForm onSuccess={handleAuthSuccess} />
              <View style={{ height: 12 }} />
              <AppButton title={t('signUp')} onPress={() => setShowSignup(true)} tone="success" />
            </>
          ) : (
            <>
              <SignupForm onSuccess={(p) => { handleAuthSuccess(p); setShowSignup(false); }} />
              <View style={{ height: 8 }} />
              <AppButton title={t('signIn')} onPress={() => setShowSignup(false)} tone="secondary" variant="outline" />
            </>
          )}
        </View>
      )}
      {showCategories && user && (
        <CategoriesManager userId={user.id} lang={lang} onClose={() => setShowCategories(false)} />
      )}

      <Modal visible={showSettings} animationType="slide" transparent>
        <SafeAreaView style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
            <Text style={[styles.modalTitle, { color: theme.colors.textPrimary }]}>{t('settings')}</Text>
            <View style={{ height: 8 }} />
            <AppButton
              title={`👤 ${t('editProfile')}`}
              onPress={() => {
                setShowSettings(false);
                setShowEditProfile(true);
              }}
              tone="secondary"
              variant="outline"
              style={{ width: '100%' }}
              textStyle={{ width: '100%', textAlign: 'center' }}
            />
            <View style={{ height: 8 }} />
            <AppButton
              title={`🗂 ${t('manageCategories')}`}
              onPress={() => {
                setShowSettings(false);
                setShowCategories(true);
              }}
              tone="secondary"
              style={{ width: '100%' }}
              textStyle={{ width: '100%', textAlign: 'center' }}
            />
            <View style={{ height: 8 }} />
            <AppButton
              title={`🔒 ${t('changePassword')}`}
              onPress={() => {
                setShowSettings(false);
                setShowChangePassword(true);
              }}
              tone="secondary"
              variant="outline"
              style={{ width: '100%' }}
              textStyle={{ width: '100%', textAlign: 'center' }}
            />
            <View style={{ height: 8 }} />
            <AppButton
              title={loading ? t('pleaseWait') : `🚪 ${t('signOut')}`}
              onPress={async () => {
                setShowSettings(false);
                await handleSignOut();
              }}
              disabled={loading}
              tone="secondary"
              variant="outline"
              style={{ width: '100%' }}
              textStyle={{ width: '100%', textAlign: 'center' }}
            />
            <View style={{ height: 12 }} />
            <AppButton
              title={t('close')}
              onPress={() => setShowSettings(false)}
              tone="secondary"
              variant="outline"
              style={{ width: '100%' }}
              textStyle={{ width: '100%', textAlign: 'center' }}
            />
          </View>
        </SafeAreaView>
      </Modal>

      <Modal visible={showEditProfile} animationType="slide" transparent={false}>
        <SafeAreaView style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center' }]}>
          <EditProfileForm
            user={user}
            lang={lang}
            onSave={handleSaveProfile}
            onDeleteAccount={handleAccountDeleted}
            onClose={() => setShowEditProfile(false)}
          />
        </SafeAreaView>
      </Modal>
      
        <Modal visible={showChangePassword} animationType="slide" transparent={false}>
          <SafeAreaView style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center' }]}> 
            <ChangePasswordForm onClose={() => setShowChangePassword(false)} defaultBackend={undefined} fullScreen={false} />
          </SafeAreaView>
        </Modal>
      </View>
    </View>
    </I18nContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  cardHeaderWeb: { justifyContent: 'center', gap: 12 } as any,
  headerIconBox: { width: 34, height: 34 },
  headerIcon: { width: '100%', height: '100%', resizeMode: 'contain' },
  headerRight: { flexDirection: 'row', alignItems: 'center' },
  langButton: { minHeight: 34, paddingVertical: 6, paddingHorizontal: 10 },
  langButtonText: { fontSize: 12 },
  menuButton: { minHeight: 34, paddingVertical: 6, paddingHorizontal: 10 },
  menuButtonText: { fontSize: 16, fontWeight: '900' },
  headerTitle: { fontSize: 20, fontWeight: '900', letterSpacing: 0.4 },
  headerSubtitle: { fontSize: 12, fontWeight: '800', letterSpacing: 0.2, marginTop: 2 },
  headerPowered: { fontSize: 11, fontWeight: '700', letterSpacing: 0.1, marginTop: 1 },
  text3dBack: {
    position: 'absolute',
    left: 1,
    top: 1,
    opacity: 0.22,
    textShadowColor: theme.colors.textPrimary,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  text3dFront: {
    opacity: 1,
    textShadowColor: theme.colors.textPrimary,
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 6,
  },
  brandIcon: { fontSize: 24, marginRight: 10 },
  brandName: { fontSize: 18, fontWeight: '800' },
  brandTagline: { fontSize: 12, fontWeight: '600' },
  content: { flex: 1 },
  contentCenter: { alignItems: 'center', justifyContent: 'flex-start' },
  contentStretch: { alignItems: 'stretch', justifyContent: 'flex-start' },
  card: { width: '100%', maxWidth: 420, padding: 16, borderRadius: theme.radii.md, borderWidth: 1 },
  // Keep the card centered on web; stretching caused inner "flex: 1" rows (tabs) to look left-aligned.
  cardFill: { flex: 1, alignSelf: 'center' },
  logoSlot: { height: 110, borderRadius: theme.radii.md, borderWidth: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 12, paddingVertical: 10 },
  loginLogo: { width: '100%', height: '100%', resizeMode: 'contain' },
  logoSlotText: { fontSize: 12, fontWeight: '700' },
  authHeader: { alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 10, borderRadius: theme.radii.sm, borderWidth: 1 },
  authHeaderText: { fontSize: 14, fontWeight: '800' },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, borderColor: theme.colors.muted, paddingHorizontal: 8, borderRadius: theme.radii.sm, backgroundColor: theme.colors.surface, color: theme.colors.textPrimary },
  row: { flexDirection: 'row', marginTop: 12 }
  ,
  modalOverlay: { flex: 1, alignItems: 'center', justifyContent: 'flex-end', padding: 12, backgroundColor: 'rgba(0,0,0,0.35)' },
  modalCard: { width: '100%', maxWidth: 520, borderRadius: 12, borderWidth: 1, padding: 14 },
  modalTitle: { fontSize: 16, fontWeight: '900' },
});

// Removed duplicate default export (kept `KakeiboApp` as default).
// If you want a small fallback/navigator component, add it here as a named export.

export function FallbackApp() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Text>Kakeibo — personal finance (expo)</Text>
    </View>
  );
}
